import React, { useEffect, useRef } from 'react';
import { X, SkipForward } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Lightweight inline video player for short-form Rowdy Message clips.
 * Opens over the current page, autoplays on mount, closes on backdrop tap,
 * `Escape`, or the close button. No new routes, no navigation — the user
 * stays exactly where they were on Today.
 *
 * Props:
 *   open           — bool
 *   item           — content record { title, videoUrl, videoAssetId, coverImageUrl }
 *   onClose        — function
 *   onEnded        — optional; called when the video reaches natural end. If
 *                    it returns a "next" item, the modal auto-advances by
 *                    calling `onNext(nextItem)` — enabling a watch-them-all
 *                    flow for the 60 Seconds With Rowdy rail.
 *   onNext         — optional; called with the next item to swap the modal in-place.
 */
const VideoModal = ({ open, item, onClose, onEnded, onNext }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Reset the <video> element whenever the item changes (auto-advance) so
  // the browser reliably fetches + autoplays the new src.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !open || !item) return;
    try { v.load(); v.play().catch(() => {}); } catch { /* ignore */ }
  }, [open, item?.id]);

  // Cross-media mutual exclusion — pause this video if the global audio
  // player (or another video instance) announces it's starting.
  useEffect(() => {
    if (!open) return;
    if (typeof window === 'undefined') return;
    const onOtherStart = (e) => {
      const src = e?.detail?.source;
      const v = videoRef.current;
      if (!v || src === 'video') return;
      try { v.pause(); } catch { /* ignore */ }
    };
    window.addEventListener('rbr:media-start', onOtherStart);
    return () => window.removeEventListener('rbr:media-start', onOtherStart);
  }, [open]);

  if (!open || !item) return null;

  const src = item.videoUrl
    || (item.videoAssetId ? `${API_URL}/api/media/${item.videoAssetId}` : null);

  const handleEnded = () => {
    // Ask the caller for the next clip in the playlist. If they return one,
    // let the parent swap it in via onNext; otherwise close cleanly.
    const nextItem = onEnded?.(item);
    if (nextItem && onNext) {
      onNext(nextItem);
    } else {
      onClose?.();
    }
  };

  const handleSkip = () => {
    const nextItem = onEnded?.(item);
    if (nextItem && onNext) onNext(nextItem);
    else onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="video-modal"
    >
      <div className="relative w-full max-w-3xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close video"
          data-testid="video-modal-close"
          className="absolute -top-10 right-0 md:-top-2 md:-right-12 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        {src ? (
          <video
            ref={videoRef}
            src={src}
            poster={item.posterImageUrl || item.coverImageUrl}
            controls
            autoPlay
            playsInline
            onPlay={() => {
              // Mutual-exclusion signal so the global AudioContext pauses.
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('rbr:media-start', {
                  detail: { source: 'video', src },
                }));
              }
            }}
            onEnded={handleEnded}
            className="w-full rounded-2xl shadow-2xl bg-black"
            data-testid="video-modal-player"
          />
        ) : (
          <div className="rounded-2xl bg-black/70 text-white p-8 text-center">
            <p>This clip has no video attached yet.</p>
          </div>
        )}
        <div className="mt-3 flex items-center justify-center gap-3">
          <p
            className="text-center text-white/90 text-sm truncate max-w-[70%]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {item.title}
          </p>
          {onEnded && (
            <button
              type="button"
              onClick={handleSkip}
              data-testid="video-modal-skip"
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/90 text-[11px] font-semibold uppercase tracking-wider transition-colors"
            >
              <SkipForward className="w-3 h-3" /> Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
